'use client';

import { useCallback, useEffect, useRef } from 'react';
import { isHandlingAuth0Response, useEnterAuth } from '@enter/auth';
import { useMixpanel } from '@/contexts/mixpanel';
import { useEnterHomeLogin } from '@/contexts/auth/use-enter-home-login';
import {
  createEnterHomeAuthAttempt,
  createEnterHomeAuthTelemetry,
  type EnterHomeAuthAttempt,
} from '@/contexts/auth/login-analytics';
import { createEnterAppAuthState } from '@/contexts/auth/auth-navigation';

type GoogleCredentialResponse = {
  credential: string;
};

type GooglePromptMomentNotification = {
  isDisplayed: () => boolean;
  isNotDisplayed: () => boolean;
  getNotDisplayedReason: () => string;
  isSkippedMoment: () => boolean;
  getSkippedReason: () => string;
  isDismissedMoment: () => boolean;
  getDismissedReason: () => string;
};

type GoogleIdentityServices = {
  accounts: {
    id: {
      initialize: (config: {
        client_id: string;
        callback: (response: GoogleCredentialResponse) => void;
        auto_select?: boolean;
        cancel_on_tap_outside?: boolean;
        itp_support?: boolean;
      }) => void;
      prompt: (callback?: (notification: GooglePromptMomentNotification) => void) => void;
    };
  };
};

declare global {
  interface Window {
    google?: GoogleIdentityServices;
  }
}

interface GoogleOneTapProps {
  enabled?: boolean;
  delay?: number;
}

const GOOGLE_IDENTITY_SERVICES_URL = 'https://accounts.google.com/gsi/client';
const GOOGLE_IDENTITY_SERVICES_TIMEOUT_MS = 5000;
const GOOGLE_IDENTITY_SERVICES_IDLE_TIMEOUT_MS = 1200;
const GOOGLE_ONE_TAP_EXCLUDED_PATHS = ['/code', '/docs'];

let googleIdentityServicesPromise: Promise<void> | null = null;

function isGoogleIdentityServicesReady(): boolean {
  return Boolean(window.google?.accounts?.id);
}

function shouldSkipGoogleOneTap(pathname: string, search: string): boolean {
  return (
    isHandlingAuth0Response(search) ||
    GOOGLE_ONE_TAP_EXCLUDED_PATHS.some(
      (path) => pathname === path || pathname.startsWith(`${path}/`),
    )
  );
}

function loadGoogleIdentityServices(): Promise<void> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new Error('Google Identity Services requires a browser environment.'));
  }

  if (isGoogleIdentityServicesReady()) {
    return Promise.resolve();
  }

  if (googleIdentityServicesPromise) {
    return googleIdentityServicesPromise;
  }

  googleIdentityServicesPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[src*="accounts.google.com/gsi/client"]`,
    );
    const script = existingScript ?? document.createElement('script');
    let settled = false;

    const cleanup = () => {
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };

    const settle = (error?: Error) => {
      if (settled) {
        return;
      }

      settled = true;
      cleanup();

      if (error) {
        reject(error);
      } else {
        resolve();
      }
    };

    const handleLoad = () => {
      if (isGoogleIdentityServicesReady()) {
        settle();
        return;
      }

      settle(new Error('Google Identity Services loaded without the expected API.'));
    };

    const handleError = () => {
      settle(new Error('Failed to load Google Identity Services.'));
    };

    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });

    const timeoutId = window.setTimeout(() => {
      settle(new Error('Google Identity Services timed out while loading.'));
    }, GOOGLE_IDENTITY_SERVICES_TIMEOUT_MS);

    if (!existingScript) {
      script.src = GOOGLE_IDENTITY_SERVICES_URL;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  }).catch((error: unknown) => {
    googleIdentityServicesPromise = null;
    throw error;
  });

  return googleIdentityServicesPromise;
}

function scheduleIdleTask(callback: () => void): () => void {
  if (typeof window.requestIdleCallback === 'function') {
    const idleCallbackId = window.requestIdleCallback(callback, {
      timeout: GOOGLE_IDENTITY_SERVICES_IDLE_TIMEOUT_MS,
    });

    return () => window.cancelIdleCallback(idleCallbackId);
  }

  const timeoutId = window.setTimeout(callback, 0);
  return () => window.clearTimeout(timeoutId);
}

function getPromptResult(notification: GooglePromptMomentNotification): {
  result: 'displayed' | 'not_displayed' | 'skipped' | 'dismissed';
  reason?: string;
} {
  if (notification.isDisplayed()) return { result: 'displayed' };
  if (notification.isNotDisplayed()) {
    return { result: 'not_displayed', reason: notification.getNotDisplayedReason() };
  }
  if (notification.isSkippedMoment()) {
    return { result: 'skipped', reason: notification.getSkippedReason() };
  }
  return {
    result: 'dismissed',
    reason: notification.isDismissedMoment() ? notification.getDismissedReason() : 'unknown',
  };
}

function extractEmailFromCredential(credential: string): string | null {
  try {
    const payload = credential.split('.')[1];
    if (!payload) {
      return null;
    }

    let base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padding = base64.length % 4;
    if (padding) {
      base64 += '='.repeat(4 - padding);
    }

    const decodedPayload = JSON.parse(atob(base64)) as {
      email?: string;
      sub?: string;
    };

    return decodedPayload.email || decodedPayload.sub || null;
  } catch {
    return null;
  }
}

export function GoogleOneTap({ enabled = true, delay = 500 }: GoogleOneTapProps) {
  const { isAuthenticated, isLoading } = useEnterAuth();
  const { login } = useEnterHomeLogin();
  const { getDistinctId, logger } = useMixpanel();
  const initializedClientIdRef = useRef<string | null>(null);
  const attemptRef = useRef<EnterHomeAuthAttempt | null>(null);
  const telemetryRef = useRef<ReturnType<typeof createEnterHomeAuthTelemetry> | null>(null);
  const hasPromptedRef = useRef(false);
  const credentialHandlerRef = useRef<
    ((response: GoogleCredentialResponse) => Promise<void>) | null
  >(null);

  const googleClientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;

  const handleCredentialResponse = useCallback(
    async (response: GoogleCredentialResponse) => {
      const email = extractEmailFromCredential(response.credential);
      const attempt =
        attemptRef.current ??
        createEnterHomeAuthAttempt({
          entry: 'direct_link',
          loginIntent: 'login',
          loginType: 'google_one_tap',
          mixpanelDistinctId: getDistinctId(),
          persist: false,
        });
      attemptRef.current = attempt;
      const telemetry = telemetryRef.current ?? createEnterHomeAuthTelemetry(attempt, logger);
      telemetryRef.current = telemetry;
      telemetry('auth_stage_start', 'one_tap_credential');
      logger('auth_onetap_click', {
        auth_trace_id: attempt.authTraceId,
        entry: attempt.entry,
        pathname: window.location.pathname,
      });
      telemetry('auth_stage_success', 'one_tap_credential');

      try {
        await login({
          authorizationParams: {
            connection: 'google-oauth2',
            login_hint: email || undefined,
          },
          appState: createEnterAppAuthState(),
          analyticsAttempt: attempt,
          authEntry: attempt.entry,
          loginIntent: 'login',
          loginType: 'google_one_tap',
        });
      } catch (error) {
        console.error('Failed to handle Google credential:', error);
      }
    },
    [getDistinctId, logger, login],
  );

  useEffect(() => {
    credentialHandlerRef.current = handleCredentialResponse;
  }, [handleCredentialResponse]);

  const initializeGoogleOneTap = useCallback((): boolean => {
    const googleIdentityServices = window.google;

    if (!googleClientId || !googleIdentityServices?.accounts?.id) {
      return false;
    }

    if (initializedClientIdRef.current === googleClientId) {
      return true;
    }

    try {
      googleIdentityServices.accounts.id.initialize({
        client_id: googleClientId,
        callback: (response) => {
          void credentialHandlerRef.current?.(response);
        },
        auto_select: false,
        cancel_on_tap_outside: true,
        itp_support: true,
      });
      initializedClientIdRef.current = googleClientId;
      return true;
    } catch (error) {
      console.error('Failed to initialize Google One Tap:', error);
      return false;
    }
  }, [googleClientId]);

  useEffect(() => {
    const canPrompt =
      enabled &&
      Boolean(googleClientId) &&
      !isLoading &&
      !isAuthenticated &&
      !shouldSkipGoogleOneTap(window.location.pathname, window.location.search);

    if (!canPrompt || hasPromptedRef.current) {
      return;
    }

    const attempt = createEnterHomeAuthAttempt({
      entry: 'direct_link',
      loginIntent: 'login',
      loginType: 'google_one_tap',
      mixpanelDistinctId: getDistinctId(),
      persist: false,
    });
    attemptRef.current = attempt;
    const telemetry = createEnterHomeAuthTelemetry(attempt, logger);
    telemetryRef.current = telemetry;

    let cancelled = false;
    let promptTimerId: number | undefined;
    const cancelIdleTask = scheduleIdleTask(() => {
      telemetry('auth_stage_start', 'one_tap_sdk_load');
      void loadGoogleIdentityServices()
        .then(() => {
          telemetry('auth_stage_success', 'one_tap_sdk_load');
          if (cancelled || !initializeGoogleOneTap()) {
            return;
          }

          promptTimerId = window.setTimeout(() => {
            const googleIdentityServices = window.google;

            if (
              cancelled ||
              hasPromptedRef.current ||
              isAuthenticated ||
              initializedClientIdRef.current !== googleClientId ||
              !googleIdentityServices?.accounts?.id
            ) {
              return;
            }

            try {
              telemetry('auth_stage_start', 'one_tap_prompt');
              googleIdentityServices.accounts.id.prompt((notification) => {
                const promptResult = getPromptResult(notification);
                logger('auth_onetap_expose', {
                  auth_trace_id: attempt.authTraceId,
                  ...promptResult,
                  duration_ms: Math.max(0, Math.round(performance.now())),
                });
                telemetry('auth_stage_success', 'one_tap_prompt');
              });
              hasPromptedRef.current = true;
            } catch (error) {
              console.error('Failed to prompt Google One Tap:', error);
            }
          }, delay);
        })
        .catch((error: unknown) => {
          if (!cancelled) {
            console.error('Failed to load Google Identity Services:', error);
          }
        });
    });

    return () => {
      cancelled = true;
      cancelIdleTask();
      if (promptTimerId !== undefined) {
        window.clearTimeout(promptTimerId);
      }
    };
  }, [
    delay,
    enabled,
    getDistinctId,
    googleClientId,
    initializeGoogleOneTap,
    isAuthenticated,
    isLoading,
    logger,
  ]);

  return null;
}
